import React, { useState, useMemo } from 'react'
import { useProducts } from '../hooks/useProducts'
import { useContent } from '../contexts/ContentContext'
import ProductCard from './ProductCard'
import { Product } from '../services/api'

// Normalizar productos para asegurar que available_sizes sea array
const normalizeProduct = (product: Product): Product => ({
  ...product,
  available_sizes: Array.isArray(product.available_sizes)
    ? product.available_sizes
    : typeof product.available_sizes === 'string'
      ? JSON.parse(product.available_sizes)
      : Object.keys(product.available_sizes || {}),
  stock_by_size: typeof product.stock_by_size === 'string'
    ? JSON.parse(product.stock_by_size)
    : product.stock_by_size || {},
})

const Products: React.FC = () => {
  const { products, isLoading } = useProducts()
  const { content } = useContent()
  const [filter, setFilter] = useState<'all' | 'boys' | 'girls'>('all')

  const whatsappNumber = content.contact?.whatsapp || '573507835575'

  // Normalizar productos (asegurar que available_sizes sea array)
  const normalizedProducts = useMemo(() => products.map(normalizeProduct), [products])

  // Filtrar productos
  const filtered = normalizedProducts.filter((p) => {
    if (filter === 'all') return true
    return p.collection === filter || p.collection === 'all'
  })

  const filterLabels = {
    all: 'Todos',
    boys: 'Niños',
    girls: 'Niñas',
  }

  return (
    <section id="catalogo" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando productos...</p>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Catálogo de Productos
          </h2>
          <p className="text-xl text-gray-600">
            Descubre nuestra selección de ropa infantil de alta calidad
          </p>
        </div>

        {/* Filtros */}
        <div className="flex justify-center gap-4 mb-12">
          {(Object.keys(filterLabels) as Array<'all' | 'boys' | 'girls'>).map((filterKey) => (
            <button
              key={filterKey}
              onClick={() => setFilter(filterKey)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                filter === filterKey
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-black'
              }`}
            >
              {filterLabels[filterKey]}
            </button>
          ))}
        </div>

        {/* Grid de Productos — siempre visible (sin opacity-0) para evitar blank en móvil */}
        {products.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-xl mb-4">📦 Catálogo en construcción</p>
            <p className="text-lg">Los productos estarán disponibles muy pronto</p>
            <p className="text-sm mt-4">Mientras tanto, puedes contactarnos por WhatsApp para más información</p>
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((product, index) => (
              <div
                key={product.id}
                className="h-full"
                style={{ animation: `fadeInUp 0.5s ease-out ${index * 0.07}s both` }}
              >
                <ProductCard product={product} whatsappNumber={whatsappNumber} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No hay productos en la categoría "{filterLabels[filter]}"</p>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">¿Necesitas ayuda para elegir?</p>
          <a
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
              'Hola Jenny Kids, necesito ayuda para elegir un producto'
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3 .97 4.29L2 22l6.29-.97c1.29.61 2.75.97 4.29.97 5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.41 0-2.73-.36-3.88-.97l-.28-.15-2.89.44.44-2.89-.15-.28C4.36 14.73 4 13.41 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8zm3.71-8.04c-.2-.1-1.18-.58-1.37-.65-.19-.07-.33-.1-.47.1-.13.2-.52.65-.63.78-.12.13-.23.15-.43.05-.2-.1-.84-.31-1.6-.99-.59-.53-.99-1.19-1.1-1.39-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.04-.25-.02-.35-.07-.1-.47-1.13-.64-1.55-.17-.41-.33-.36-.47-.37-.12 0-.25-.01-.39-.01-.13 0-.33.05-.5.25-.18.2-.66.64-.66 1.56 0 .92.68 1.81.77 1.94.09.13 1.27 1.93 3.06 2.71.43.19.76.3.99.38.43.13.82.11 1.13.07.35-.05 1.06-.43 1.21-.85.15-.42.15-.78.1-.85-.04-.07-.15-.11-.31-.2z" />
            </svg>
            Contacta con nosotros
          </a>
        </div>
        </>
        )}
      </div>
    </section>
  )
}

export default Products
